import React, { useMemo } from 'react';
import { DynamicTableV2 } from '@packages/table-builder';
import { Page } from '@packages/ui/layout';
import { Spinner } from '@packages/ui/primitives/Spinner';
import { Stack } from '@packages/ui/layout/Stack';
import { spacing } from '@packages/ui/tokens';
import { axiosInstance } from '@/services/apiInterceptor';
import { registerCharterStockClassActionAliases } from '@apps/customer/domains/charter-stock-class/registerActionAliases';
import { buildCharterStockClassesTable } from './schema';
import { useCharterStockClassesSession } from './hooks/useCharterStockClassesSession';
import { CharterFilingBanners } from './components/CharterFilingBanners';

registerCharterStockClassActionAliases();

export default function CharterStockClassesPage() {
  const session = useCharterStockClassesSession();
  const {
    filings,
    hasCharter,
    activeFiling,
    selectedFiling,
    isViewingHistory,
    filingOptions,
    tableRows,
    tableInfo,
    tableFilters,
    totalCount,
    loading,
    isSaveLoading,
    onFilterChange,
    onSearchChange,
    onTablePagingChange,
    showActiveFiling,
    handleSave,
    handleDocumentDownload,
    deleteFiling,
  } = session;

  const schema = useMemo(
    () => buildCharterStockClassesTable(filingOptions),
    [filingOptions],
  );

  if (loading === 'loading' && filings == null) {
    return (
      <div className='flex w-full items-center justify-center min-h-[50vh]'>
        <Spinner size='lg' semanticId='charterStockClassesLoading' />
      </div>
    );
  }

  return (
    <Page
      semanticId='charterStockClassesPage'
      title='Charter & Stock Classes'
      subtitle='Historical ledger of legal filing events and authorized capital.'
      style={{ padding: 0 }}
    >
      <Stack gap={spacing.lg}>
        {hasCharter && !isViewingHistory && (
          <CharterFilingBanners
            activeFiling={activeFiling}
            isViewingHistory={false}
            onDocumentDownload={handleDocumentDownload}
            onDeleteFiling={deleteFiling}
            onShowActive={showActiveFiling}
          />
        )}

        {isViewingHistory && selectedFiling && (
          <CharterFilingBanners
            selectedFiling={selectedFiling}
            isViewingHistory
            onDocumentDownload={handleDocumentDownload}
            onDeleteFiling={deleteFiling}
            onShowActive={showActiveFiling}
          />
        )}

        {!hasCharter ? (
          <DynamicTableV2
            key='charter-stock-classes-empty'
            namespace='charterStockClasses'
            schema={schema}
            data={[]}
            loading='success'
            totalCount={0}
            axiosInstance={axiosInstance}
            filter={tableFilters}
            onFilterChange={onFilterChange}
            onSearchChange={onSearchChange}
          />
        ) : (
          <DynamicTableV2
            key={`charter-stock-classes-${activeFiling?.id ?? 'none'}`}
            namespace='charterStockClasses'
            schema={schema}
            data={tableRows}
            loading={loading}
            totalCount={totalCount}
            axiosInstance={axiosInstance}
            onSave={handleSave}
            isSaveLoading={isSaveLoading}
            filter={tableFilters}
            onFilterChange={onFilterChange}
            onSearchChange={onSearchChange}
            tableInfo={{
              skipCount: tableInfo.skipCount,
              maxResultCount: tableInfo.maxResultCount,
              sorting: tableInfo.sorting,
            }}
            setTableInfo={onTablePagingChange}
          />
        )}
      </Stack>
    </Page>
  );
}
